import { jobTitleLabels, type TeamMember, type TeamRole } from "@teamna/shared";
import { useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Avatar,
  Badge,
  Button,
  ChevronRightIcon,
  CopyIcon,
  DataTable,
  ErrorState,
  Field,
  FormSection,
  InlineError,
  Notice,
  Panel,
  RadioGroup,
  RowMenu,
  showToast,
  type DataTableColumn,
  type RowMenuItem,
} from "../../components/index.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import {
  useAddTeamMember,
  useMarkSetupStep,
  useRemoveTeamMember,
  useRevokeTeamInvitation,
  useTeamFresh,
  useSendTeamInvitation,
  useTeam,
  useUpdateTeamRole,
} from "@teamna/web-client/hooks/use-setup.ts";
import { formatDateTime } from "@teamna/web-client/lib/format.ts";
import { SaveBar, SetupFrame } from "./frame.tsx";
import { phoneText } from "../families/model.ts";

/**
 * Setup step 2, and the Team page until slice E (mobile spec 5.1 step 2).
 *
 * A table of the people who run the business: the owner pinned with a fixed role, everyone else
 * with a "More" menu (change the role, the invitation, Remove; Staff page redesign 2026-11-03).
 * "Add a person" puts a person on the list with a role and sends them a personal invitation on
 * WhatsApp (migration 0054). Each person who is not in the app yet has "Invitation": the status,
 * Copy link, a new invitation, and Revoke. "Done" marks the step, the same `POST /setup/team` the
 * mobile onboarding sends.
 */
export function SetupTeamScreen() {
  return <SetupFrame step="team">{(businessId) => <TeamStep businessId={businessId} />}</SetupFrame>;
}

/** The step: the team manager and "Done", which marks the step. */
function TeamStep({ businessId }: { businessId: string }) {
  const mark = useMarkSetupStep(businessId);
  return (
    <div className="flex flex-col gap-6">
      <TeamManager businessId={businessId} />
      <div>
        <SaveBar note="You can come back and change the team at any time.">
          <Button
            loading={mark.isPending}
            onClick={() =>
              mark.mutate("team", {
                onSuccess: () => showToast("Team step done"),
              })
            }
          >
            Done, the team is right
          </Button>
        </SaveBar>
        {mark.error ? <InlineError error={mark.error} /> : null}
      </div>
    </div>
  );
}

/** One person across a revoke and a new invitation, which change the row id. */
function personKey(row: TeamMember): string {
  return row.phoneE164 ?? row.id;
}

/** Not the owner, not you, not in the app yet: the person has an invitation to manage. */
function hasInvitation(row: TeamMember): boolean {
  return row.status !== "active" && row.role !== "owner" && !row.isSelf;
}

type InvitationState = { label: string; tone: "confirmed" | "cancelled" | "late" | "quiet"; detail: string | null };

/** What the Status column and the Invitation panel say about one person. */
export function invitationState(row: TeamMember): InvitationState {
  if (row.status === "active") return { label: "In the app", tone: "confirmed", detail: null };
  const invitation = row.invitation ?? null;
  // `removed` and `replaced` are never the last invitation of a person on the list; as no invitation, to be safe.
  if (!invitation || invitation.status === "removed" || invitation.status === "replaced") return { label: "Not invited", tone: "late", detail: null };
  if (invitation.status === "revoked") {
    return { label: "Revoked", tone: "cancelled", detail: invitation.revokedAt ? formatDateTime(invitation.revokedAt) : null };
  }
  if (invitation.whatsappStatus !== "sent") return { label: "Not sent", tone: "late", detail: "Copy the link" };
  return { label: "Invited", tone: "quiet", detail: formatDateTime(invitation.sentAt) };
}

/** A Desk page; Control Center has no route for it and turns `memberLink` off, so the path is a plain string. */
const memberPage = (userId: string): string => `/owner/team/${userId}`;

/** The roles a person can get here, in the order the owner reads them. The owner role never changes. */
const PICKABLE_ROLES: TeamRole[] = ["staff", "frontdesk", "admin"];

function teamColumns({
  roleName,
  updateRole,
  setRemoving,
  setInviting,
  readOnly,
  memberLink,
}: {
  roleName: (role: TeamMember["role"]) => string;
  updateRole: ReturnType<typeof useUpdateTeamRole>;
  setRemoving: (member: TeamMember) => void;
  setInviting: (member: TeamMember) => void;
  /** The list only: the role as words, no invitation and no Remove (the front desk). */
  readOnly: boolean;
  /** The row opens the person's page (`/owner/team/:userId`), with an arrow. Only for a person with an account. */
  memberLink: boolean;
}): DataTableColumn<TeamMember>[] {
  const columns: DataTableColumn<TeamMember>[] = [
    {
      id: "name",
      header: "Name",
      cell: (row) => (
        <span className="flex min-w-0 items-center gap-2.5">
          <Avatar name={row.name} photoUrl={row.photoUrl ?? null} size={32} />
          <span className="font-semibold text-ink">
            <span dir="auto">{row.name}</span>
            {row.isSelf ? <span className="ml-1.5 font-normal text-ink-3">(you)</span> : null}
          </span>
        </span>
      ),
      sortValue: (row) => row.name,
    },
    { id: "phone", header: "Mobile", cell: (row) => (row.phoneE164 ? phoneText(row.phoneE164) : row.phoneMasked), hideOnPhone: true },
    {
      id: "role",
      header: "Role",
      cell: (row) => (
        <span>
          {roleName(row.role)}
          {row.jobTitle ? <span className="text-ink-3"> · {jobTitleLabels[row.jobTitle]}</span> : null}
        </span>
      ),
      sortValue: (row) => roleName(row.role),
    },
    {
      id: "status",
      header: "Status",
      cell: (row) => {
        const state = invitationState(row);
        return (
          <span className="flex flex-col items-start gap-0.5">
            <Badge tone={state.tone}>{state.label}</Badge>
            {state.detail ? <span className="text-[12px] text-ink-2">{state.detail}</span> : null}
          </span>
        );
      },
      hideOnPhone: true,
    },
  ];
  if (memberLink) {
    columns.push({
      id: "open",
      header: "",
      width: "36px",
      align: "end",
      cell: (row) => (opensRow(row, readOnly) ? <ChevronRightIcon size={18} className="text-ink-3" /> : null),
    });
  }
  if (readOnly) return columns;
  columns.push({
    id: "actions",
    header: "",
    align: "end",
    width: "56px",
    cell: (row) => {
      if (row.role === "owner" || row.isSelf) return null;
      const items: RowMenuItem[] = PICKABLE_ROLES.filter((role) => role !== row.role).map((role) => ({
        label: `Make ${roleName(role).toLowerCase()}`,
        onSelect: () =>
          updateRole.mutate({ id: row.id, role }, { onSuccess: () => showToast(`${row.name} is ${roleName(role).toLowerCase()} now`) }),
      }));
      if (hasInvitation(row)) items.push({ label: "Invitation", onSelect: () => setInviting(row) });
      items.push({ label: "Remove", tone: "danger", onSelect: () => setRemoving(row) });
      return <RowMenu label={`More for ${row.name}`} items={items} />;
    },
  });
  return columns;
}

/**
 * What a row click opens on the Staff page: the person's page when they have an account, else their
 * invitation (a person who is invited and not in the app yet). Nothing for the front desk's list.
 */
function opensRow(row: TeamMember, readOnly: boolean): boolean {
  return row.userId !== null || (!readOnly && hasInvitation(row));
}

/** The role as the owner reads it: the business's own staff word for `staff`, "Front desk" for `frontdesk` (T1). */
export function teamRoleName(role: TeamMember["role"], staffCapital: string): string {
  if (role === "owner") return "Owner";
  if (role === "admin") return "Admin";
  return role === "frontdesk" ? "Front desk" : staffCapital;
}

/**
 * The team table, the add-by-number form, what each role can do, and the remove confirm. The
 * setup step and the Team page (`/owner/team`) both show it.
 */
export function TeamManager({
  businessId,
  onAdded,
  onRemoved,
  readOnly = false,
  memberLink = false,
  addPanel,
}: {
  businessId: string;
  /** Each row opens the person's page. Desk's Staff page only: setup and Control Center have no such page. */
  memberLink?: boolean;
  /**
   * "Add a person" in a side panel, opened by a button the page owns (the Staff page header). Left
   * out: the add form is on the page under the list (setup step 2).
   */
  addPanel?: { open: boolean; onClose: () => void };
  /** The list and what each role can do, with no add, change or remove: the front desk (Settings, Front desk, Team). */
  readOnly?: boolean;
  /** A person was added, with their row id. The first-run Team step counts it. */
  onAdded?: (id: string) => void;
  /** A person was removed, with their row id. */
  onRemoved?: (id: string) => void;
}) {
  const { label } = useLabels();
  const team = useTeam(businessId);
  const updateRole = useUpdateTeamRole(businessId);
  const remove = useRemoveTeamMember(businessId);
  // A key, not the row: the panel reads the row from the list, so it shows the state after a send.
  // The number, not the id: Revoke takes the team row away, and the person comes back as a contact
  // with another id (and a new invitation turns them back into a team row).
  const [invitingKey, setInvitingKey] = useState<string | null>(null);
  const inviting = invitingKey ? ((team.data ?? []).find((row) => personKey(row) === invitingKey) ?? null) : null;

  const [removing, setRemoving] = useState<TeamMember | null>(null);
  const navigate = useNavigate();

  const staffWord = label("staff");
  const staffCapital = label("staff", { capital: true });

  /** After an add: a toast, or the invitation panel when the WhatsApp did not go (the link to send). */
  function added(member: TeamMember) {
    onAdded?.(member.id);
    addPanel?.onClose();
    if (member.invitation?.whatsappStatus === "sent") {
      showToast(`${member.name} added. Invitation sent on WhatsApp.`);
    } else {
      // The panel shows the link once the list read after the add has it.
      showToast(`${member.name} added`);
      setInvitingKey(personKey(member));
    }
  }

  const roleName = (role: TeamMember["role"]) => teamRoleName(role, staffCapital);

  const columns = teamColumns({
    roleName,
    updateRole,
    setRemoving,
    setInviting: (row) => setInvitingKey(personKey(row)),
    readOnly,
    memberLink,
  });

  return (
    <div className="flex flex-col gap-6">
      {/* The list on the screen is the last good read. A failed read says so, with a retry, also when
          old rows stay on the screen (a person just added is not in them; internal review F12.2). */}
      {team.error && team.data && !team.isFetching ? (
        <Notice tone="danger" title="Teamna could not read the team list">
          <span className="flex flex-col items-start gap-2">
            The list below can be old.
            <Button variant="secondary" onClick={() => void team.refetch()}>
              Try again
            </Button>
          </span>
        </Notice>
      ) : null}
      {team.error && !team.data ? (
        <ErrorState error={team.error} onRetry={() => void team.refetch()} />
      ) : (
        <DataTable
          caption="Team"
          columns={columns}
          rows={team.data ?? []}
          rowKey={(row) => row.id}
          isLoading={team.isLoading}
          {...(memberLink
            ? {
                onRowClick: (row: TeamMember) => {
                  if (row.userId) void navigate({ to: memberPage(row.userId) });
                  else if (opensRow(row, readOnly)) setInvitingKey(personKey(row));
                },
                canOpenRow: (row: TeamMember) => opensRow(row, readOnly),
                rowLabel: (row: TeamMember) => (row.userId ? `Open ${row.name}` : `Invitation for ${row.name}`),
              }
            : {})}
          empty={{
            title: "Only you so far",
            message: `Add your ${label("staff", { plural: true })} and any admin.`,
          }}
          countLabel={(count) => `${count} ${count === 1 ? "person" : "people"}`}
        />
      )}

      {readOnly ? null : addPanel ? (
        <Panel
          open={addPanel.open}
          onClose={addPanel.onClose}
          title="Add a person"
          subtitle="Teamna sends them an invitation on WhatsApp, with a personal link. They sign in with this number to join."
        >
          {addPanel.open ? <AddPersonForm businessId={businessId} staffWord={staffWord} onAdded={added} /> : null}
        </Panel>
      ) : (
        <FormSection
          title="Add a person"
          description="Teamna sends each person an invitation on WhatsApp, with a personal link. They sign in with this number to join."
        >
          <div className="py-4">
            <AddPersonForm businessId={businessId} staffWord={staffWord} onAdded={added} />
          </div>
        </FormSection>
      )}

      <InvitationPanel
        businessId={businessId}
        member={inviting}
        staffWord={staffWord}
        onClose={() => setInvitingKey(null)}
      />

      <Panel
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={removing ? `Remove ${removing.name}?` : "Remove"}
        subtitle="They lose access to the business at once."
        actions={
          <>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => {
                if (!removing) return;
                const who = removing.name;
                const id = removing.id;
                remove.mutate(id, {
                  onSuccess: () => {
                    setRemoving(null);
                    onRemoved?.(id);
                    showToast(`${who} removed`);
                  },
                });
              }}
            >
              Remove
            </Button>
            <Button variant="secondary" onClick={() => setRemoving(null)}>
              Keep
            </Button>
          </>
        }
      >
        {remove.error ? <InlineError error={remove.error} /> : null}
      </Panel>
    </div>
  );
}

/**
 * Add a person: name, mobile and role, then one button. Each role says what it can do, so the owner
 * picks with the facts in front of them (this replaced "What each role can do" under the list and
 * one add button per role, Staff page redesign 2026-11-03). The role starts on staff, the usual pick.
 */
export function AddPersonForm({
  businessId,
  staffWord,
  onAdded,
}: {
  businessId: string;
  staffWord: string;
  onAdded: (member: TeamMember) => void;
}) {
  const { label } = useLabels();
  const add = useAddTeamMember(businessId);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<TeamRole>("staff");
  const [error, setError] = useState<unknown>(null);
  const canAdd = name.trim().length > 0 && phone.trim().length >= 7 && !add.isPending;
  const members = label("member", { plural: true });

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canAdd) return;
    setError(null);
    add.mutate(
      { name: name.trim(), phoneE164: phone.trim(), role },
      {
        onSuccess: (member) => {
          setName("");
          setPhone("");
          setRole("staff");
          onAdded(member);
        },
        onError: (caught) => setError(caught),
      },
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <Field label="Name" dir="auto" value={name} placeholder="Full name" onChange={(event) => setName(event.target.value)} />
      <Field
        label="Mobile number"
        hint="A UAE number can start with 05."
        value={phone}
        inputMode="tel"
        placeholder="050 123 4567"
        onChange={(event) => setPhone(event.target.value)}
      />
      <RadioGroup<TeamRole>
        legend="Role"
        value={role}
        onChange={setRole}
        options={[
          {
            value: "staff",
            label: staffWord[0]!.toUpperCase() + staffWord.slice(1),
            hint: `Sees the schedule, the lists of ${label("participant", { plural: true })} and the messages for their sessions.`,
          },
          {
            value: "frontdesk",
            label: "Front desk",
            hint: `Books for ${members}, records payments at the desk and checks people in. Cannot change packages, rules, the timetable or the team. You choose what more it can do in Settings, Front desk.`,
          },
          { value: "admin", label: "Admin", hint: `Runs the business with you: schedule, ${members}, messages.` },
        ]}
      />
      {error ? <InlineError error={error} /> : null}
      <div>
        <Button type="submit" disabled={!canAdd} loading={add.isPending}>
          Add and send invitation
        </Button>
      </div>
    </form>
  );
}

/**
 * The invitation of one person who is not in the app yet: what happened to it, the personal link to
 * copy, a new invitation (the old link stops working), and Revoke (no access until a new one).
 */
function InvitationPanel({
  businessId,
  member,
  staffWord,
  onClose,
}: {
  businessId: string;
  member: TeamMember | null;
  staffWord: string;
  onClose: () => void;
}) {
  const { label } = useLabels();
  const send = useSendTeamInvitation(businessId);
  const revoke = useRevokeTeamInvitation(businessId);
  const [manualCopy, setManualCopy] = useState(false);
  // Revoke takes the access and the groups away, so it asks first (internal review F1.8).
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  // The list on the screen comes only from server reads (the team hooks never write their answers
  // into it). The link and Revoke show only when the last read finished after every team write and
  // did not fail (internal review F7.2 to F11.2).
  const team = useTeam(businessId);
  const fresh = useTeamFresh(businessId);
  // A removed or replaced link is dead: the panel treats it as no invitation, as the Status column does.
  const last = member?.invitation ?? null;
  const invitation = last && last.status !== "removed" && last.status !== "replaced" ? last : null;
  const state = member ? invitationState(member) : null;
  const revoked = invitation?.status === "revoked";
  const open = invitation?.status === "pending";
  const first = member?.name.split(/\s+/)[0] ?? "";

  function close() {
    setManualCopy(false);
    setConfirmingRevoke(false);
    send.reset();
    revoke.reset();
    onClose();
  }

  /** Some browsers refuse the clipboard. The owner must always get the link: then it shows in a field. */
  async function copyLink() {
    if (!invitation) return;
    try {
      if (!navigator.clipboard) throw new Error("No clipboard");
      await navigator.clipboard.writeText(invitation.link);
      showToast("Link copied");
    } catch {
      setManualCopy(true);
    }
  }

  function sendNew() {
    if (!member) return;
    send.mutate(member.id, {
      onSuccess: (items) => {
        const after = items.find((row) => personKey(row) === personKey(member));
        setManualCopy(false);
        if (after?.status === "active") showToast(`${member.name} is in the app now`);
        else if (after?.invitation?.whatsappStatus === "sent") showToast("Invitation sent on WhatsApp");
        else showToast("New invitation ready. Copy the link.");
      },
    });
  }

  return (
    <Panel
      open={member !== null}
      onClose={close}
      title={member ? `Invitation for ${member.name}` : "Invitation"}
      subtitle={member ? `${teamRoleName(member.role, staffWord.charAt(0).toUpperCase() + staffWord.slice(1))} · ${member.phoneE164 ? phoneText(member.phoneE164) : member.phoneMasked}` : undefined}
      actions={
        member && confirmingRevoke ? (
          <>
            <Button
              variant="danger"
              loading={revoke.isPending}
              disabled={!fresh && !revoke.isPending}
              onClick={() =>
                revoke.mutate(member.id, {
                  onSuccess: () => {
                    setConfirmingRevoke(false);
                    showToast("Invitation revoked");
                  },
                  onError: () => setConfirmingRevoke(false),
                })
              }
            >
              Revoke invitation
            </Button>
            <Button variant="secondary" onClick={() => setConfirmingRevoke(false)}>
              Keep
            </Button>
          </>
        ) : member ? (
          <>
            <Button loading={send.isPending} disabled={!fresh && !send.isPending} onClick={sendNew}>
              {revoked || !invitation ? "Send invitation" : "Send a new invitation"}
            </Button>
            {open && fresh ? (
              <Button variant="danger" onClick={() => setConfirmingRevoke(true)}>
                Revoke
              </Button>
            ) : null}
            <Button variant="secondary" onClick={close}>
              Close
            </Button>
          </>
        ) : null
      }
    >
      {member && confirmingRevoke ? (
        <div className="flex flex-col gap-4 text-[14px] text-ink-2">
          <p>
            Revoke the invitation for {member.name}? The link stops working, and {first || "this person"} loses their team access.{" "}
            {first || "This person"} also comes off the {label("group", { plural: true })} you gave them.
          </p>
          <p>A new invitation gives the team access back, but not the {label("group", { plural: true })}.</p>
          {revoke.error ? <InlineError error={revoke.error} /> : null}
        </div>
      ) : member && state ? (
        <div className="flex flex-col gap-4 text-[14px] text-ink-2">
          <p className="flex items-center gap-2">
            <Badge tone={state.tone}>{state.label}</Badge>
            {invitation && !revoked ? (
              <span>
                {invitation.whatsappStatus === "sent" ? "Sent" : "Link made"} {formatDateTime(invitation.sentAt)}
              </span>
            ) : null}
          </p>
          {!invitation ? (
            <p>{first || "This person"} has no invitation yet. Send one: Teamna sends a WhatsApp with a personal link.</p>
          ) : revoked ? (
            <p>
              You revoked this invitation. The link does not work, and {first || "this person"} has no team access with this number. Send a
              new invitation to give the team access back.
            </p>
          ) : invitation.whatsappStatus === "sent" ? (
            <p>
              Teamna sent the invitation on WhatsApp. {first || "The person"} opens the link and signs in with this number to join.
            </p>
          ) : (
            <p>
              The WhatsApp message did not go. Copy the link and send it to {first || "the person"} yourself. They sign in with this number
              to join.
            </p>
          )}
          {!fresh ? (
            team.isError && !team.isFetching ? (
              <div className="flex flex-col items-start gap-2">
                <p className="text-[13px]">Teamna could not read the team list. Read it again to see the link.</p>
                <Button variant="secondary" onClick={() => void team.refetch()}>
                  Read the list again
                </Button>
              </div>
            ) : (
              <p className="text-[13px]">Teamna is reading the team list.</p>
            )
          ) : null}
          {invitation && !revoked && fresh ? (
            <div className="flex flex-col gap-2">
              <Button variant="secondary" onClick={() => void copyLink()}>
                <span className="inline-flex items-center gap-2">
                  <CopyIcon size={16} /> Copy link
                </span>
              </Button>
              {manualCopy ? (
                <Field label="Invitation link" value={invitation.link} readOnly onFocus={(event) => event.currentTarget.select()} />
              ) : null}
            </div>
          ) : null}
          {open ? (
            <p className="text-[13px]">A new invitation replaces this one: the old link stops working.</p>
          ) : null}
          {send.error ? <InlineError error={send.error} /> : null}
          {revoke.error ? <InlineError error={revoke.error} /> : null}
        </div>
      ) : null}
    </Panel>
  );
}
